import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { endorsementsInputs, endorsementsSpouseInputs } from './constants/contract-chip-inputs';
import { isEndorsementComplete } from './EndorsementUtils';
import { REQUIRES_SPOUSE_DATA } from '../../constants/civil-status';
import getCompletionStatusChipStyles, {
  getCompletionStatusChipLabel,
} from './getCompletionStatusChipStyles';

const EndorsementItem = ({
  endorsement,
  expanded,
  onExpandedChange,
  onUpdate,
  onRemove,
  canRemove = true,
}) => {
  const isComplete = isEndorsementComplete(endorsement);
  const displayName = endorsement.name?.trim() || 'Sin nombre';

  const handleInfoFunc = (fieldId, value) => {
    let processedValue = value;
    if (fieldId === 'email' || fieldId === 'spouceEmail') {
      processedValue = value.trim();
    }
    onUpdate(endorsement.id, { [fieldId]: processedValue });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandedChange(isExpanded)}
      disableGutters
      elevation={0}
      variant="bordered"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {displayName}
        </Typography>
        <Chip
          label={getCompletionStatusChipLabel(isComplete)}
          size="small"
          sx={getCompletionStatusChipStyles(isComplete)}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2 }}>
        <Stack spacing={2}>
          {endorsementsInputs.map((rowInputs) => (
            <Stack
              direction="row"
              spacing={2}
              sx={{ width: '100%' }}
              key={`endorsement-row-${rowInputs[0]?.index}`}
            >
              {rowInputs.map((input) => (
                <input.component
                  key={`endorsement-input-${input.id}-${endorsement.id}`}
                  input={input}
                  obj={endorsement}
                  handleInfoFunc={handleInfoFunc}
                />
              ))}
            </Stack>
          ))}
          {REQUIRES_SPOUSE_DATA.includes(endorsement.civilStatus) && endorsementsSpouseInputs.map((rowInputs) => (
            <Stack
              direction="row"
              spacing={2}
              sx={{ width: '100%' }}
              key={`endorsement-spouse-row-${rowInputs[0]?.index}`}
            >
              {rowInputs.map((input) => (
                <input.component
                  key={`endorsement-spouse-input-${input.id}-${endorsement.id}`}
                  input={input}
                  obj={endorsement}
                  handleInfoFunc={handleInfoFunc}
                />
              ))}
            </Stack>
          ))}
          {canRemove && (
            <Stack direction="row" justifyContent="flex-start">
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => onRemove(endorsement.id)}
              >
                Eliminar
              </Button>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

EndorsementItem.propTypes = {
  endorsement: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string,
    rut: PropTypes.string,
    nationality: PropTypes.string,
    profession: PropTypes.string,
    email: PropTypes.string,
    civilStatus: PropTypes.string,
    label: PropTypes.string,
    address: PropTypes.string,
    spouceName: PropTypes.string,
    spouceRut: PropTypes.string,
    spouceNationality: PropTypes.string,
    spouceProfession: PropTypes.string,
    spouceAddress: PropTypes.string,
    spouceEmail: PropTypes.string,
    spouceLabel: PropTypes.string,
    personeriaComments: PropTypes.string,
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  onExpandedChange: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool,
};

EndorsementItem.defaultProps = {
  canRemove: true,
};

export default EndorsementItem;
